/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ICoreContext } from '@msdyn365-commerce/core';
import { FinancingOfferStateInput, IFinancingOfferStateData } from '../../../data-actions/financing-offer-state.action';
import { fireAnalyticsLink } from '../../../Utilities/analytics/clickTrack';
import {
    getCampaignRemainingCost,
    getValidSpecialCampaign,
    IFinancingOfferFormatted,
    selectFinancingOffer
} from '../../../Utilities/select-financing-offer';
import { getQueryParamsFromQueryByName } from '../../../data-actions/utilities/utils';

export type FinancingComponentPageType = 'PDP' | 'PLP';

export interface IFinancingPerMonthComponentProps {
    pageType: FinancingComponentPageType;
    sellingPrice: number;
    // tslint:disable-next-line:no-any
    context: ICoreContext<{ [x: string]: any }>;
    priceComponent?: JSX.Element;
    // VSI Customization - START
    sizeValue?: string | undefined;
    dimensionPrice?: number | undefined;
    percentDiff?: number | undefined;
    discountAmount?: number;
    categoryName?: String | undefined,
    activeVariantId: number | undefined,
    productId: number | undefined,
    activeColorVariantId: number | undefined
    // VSI Customization - END
}

export const FinancingPerMonthComponent: React.FC<IFinancingPerMonthComponentProps> = ({
    pageType,
    sellingPrice,
    context,
    priceComponent,
    sizeValue,
    dimensionPrice,
    percentDiff,
    discountAmount,
    categoryName,
    activeVariantId,
    productId,
    activeColorVariantId
}) => {
    const selectedFinancingOffer: IFinancingOfferFormatted | undefined = selectFinancingOffer({
        limitedTimeFinancingOffers: context.app.config.limitedTimeFinancingOffers,
        everydayFinancingOffers: context.app.config.everydayFinancingOffers,
        totalPrice: sellingPrice
    });

    if (!sellingPrice) {
        return null;
    }
    const monthlyCost = selectedFinancingOffer && Math.ceil(sellingPrice / selectedFinancingOffer.numOfMonths);
    const stateUpdate: IFinancingOfferStateData = {
        isOpen: true,
        totalPrice: sellingPrice,
        financingOffer: selectedFinancingOffer
    };
    const url = new URL(window.location.href.toString());
    const variantId = Number(getQueryParamsFromQueryByName(url, 'variantid')) || undefined;
    const openModal = () => {
        fireAnalyticsLink({
            eventName: 'internal navigation',
            eventCategory: 'internal links',
            eventAction: 'to: Financing Per Month modal',
            eventLabel: `from ${pageType} Financing Per Month “Learn How” link`,
            eventNoninteraction: 'false'
        });
        context.actionContext.update(new FinancingOfferStateInput(), stateUpdate);
    };
    // format currency and strip trailing zeros
    // TODO: remove the cents portion in a dynamic way for all currencies
    const formattedMonthlyCost = monthlyCost && context.cultureFormatter.formatCurrency(monthlyCost);
    const monthlyCostStrippedZeros = formattedMonthlyCost && formattedMonthlyCost.substring(0, formattedMonthlyCost.length - 3);

    const campaign = selectedFinancingOffer && getValidSpecialCampaign(selectedFinancingOffer);
    const campaignRemainingCost =
        campaign && selectedFinancingOffer && getCampaignRemainingCost(campaign, selectedFinancingOffer, sellingPrice);

    const formattedCampaignCost =
        campaign && campaignRemainingCost && context.cultureFormatter.formatCurrency(Math.ceil(campaignRemainingCost.cost));
    const campaignCostStrippedZeros = formattedCampaignCost && formattedCampaignCost?.substring(0, formattedCampaignCost.length - 3);

    const everydayFinancingMessage = context.app.config.everydayFinancingMessage;
    const limitedFinancingMessage = context.app.config.limitedFinancingMessage;
    const limitedCampaignFinancingMessage = context.app.config.limitedCampaignFinancingMessage;

    const sellingPriceDollars = sellingPrice.toFixed(2).split('.')[0];
    const sellingPriceCents = sellingPrice.toFixed(2).split('.')[1];

    const dimensionDollars = dimensionPrice?.toFixed(2).split('.')[0];
    const dimensionCents = dimensionPrice?.toFixed(2).split('.')[1];
    const readyToShow = activeVariantId === productId || activeColorVariantId === productId || variantId === productId || pageType=== "PLP";
    const promotionalPeriod = selectedFinancingOffer?.numOfMonths;
    const validThroughDate = selectedFinancingOffer && selectedFinancingOffer.isLTO && (promotionalPeriod === 36 || promotionalPeriod === 48 || promotionalPeriod === 60) ? '' : ` Valid thru ${selectedFinancingOffer?.endDate} `;
    // tslint:disable: react-this-binding-issue jsx-no-lambda react-a11y-event-has-role
    return pageType === 'PDP' ? (
        <div className={`redesign msc-product__financing-per-month${selectedFinancingOffer ? '' : ' no-financing'}`}>
            <div className='product-price-section'>
                <div className={Number(percentDiff) > 0 && Number(percentDiff) < 100 && discountAmount !== 0 ? 'product-price' : 'product-price-black'}>

                    {Number(percentDiff) > 0 && discountAmount !== 0 ? readyToShow ?
                        <>
                            $<span className='product-dollars'>{sellingPriceDollars}</span>
                            <span className='product-cents'>{sellingPriceCents}</span>
                            <span className='product-original'>
                                <span className='product-original-price'>
                                    $<span className='product-dollars'>{dimensionDollars}</span>
                                    <span className='product-cents'>{dimensionCents}</span>
                                </span>
                                <span className='product-offer-discount'>{percentDiff && percentDiff.toFixed()}% Off</span>
                            </span></>
                        : <div className='product-price-loader'>
                            <div className='msc-waiting msc-waiting-circular price-spinner' />
                        </div> : readyToShow ? priceComponent || (
                            <>
                                $<span className='product-dollars'>{sellingPriceDollars}</span>
                                <span className='product-cents'>{sellingPriceCents}</span>
                            </>) : <div className='product-price-loader'>
                        <div className='msc-waiting msc-waiting-circular price-spinner' />
                    </div>
                    }
                </div>
            </div>
            {selectedFinancingOffer && (
                <>
                    {campaign && campaignRemainingCost ? (
                        <div className='financing-price-section has-campaign'>
                            {/* commented as per story no 21902 */}
                            {/* <div className='financing-price-label'>Pay as low as</div> */}
                            <div className='financing-price'>or {`$${Math.ceil(
                                campaign.initialMonthlyAmount
                            )}/mo for the first ${campaign.numOfDiscountedMonths} months`}</div>
                            {/* tslint:disable-next-line: react-this-binding-issue jsx-no-lambda react-a11y-event-has-role*/}
                            <div className='financing-price-info financing-price-text' onClick={e => e.stopPropagation()}>
                                {/* {`Then $${Math.ceil(campaignRemainingCost.cost)}/mo for ${
                                    campaignRemainingCost?.months
                                } months with 0% APR with Mattress Firm Credit Card.Limited Time.`} */}
                                {limitedCampaignFinancingMessage &&
                                    limitedCampaignFinancingMessage
                                        .replace('{campaignCost}', campaignCostStrippedZeros)
                                        .replace('{campaignMonths}', campaignRemainingCost?.months)}
                                <a role='button' className='financing-price-modal-toggle' onClick={openModal} href='javascript:void(0)'>
                                    <span className='learn-how'>Learn How</span>
                                    <span className='prevent-wrap'>
                                        <span aria-hidden='true'></span>
                                    </span>
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className='financing-price-section'>
                            {/* commented as per story no 21902 */}
                            {/* <div className='financing-price-label'>Pay as low as</div> */}
                            {/* <div className='financing-price financing-price-right'>
                                {pageType === 'PDP' ? `${monthlyCostStrippedZeros}/mo` : `${monthlyCostStrippedZeros} / mo`}
                            </div> */}
                            {/* tslint:disable-next-line: react-this-binding-issue jsx-no-lambda react-a11y-event-has-role*/}
                            <div className='financing-price-info financing-price-text' onClick={e => e.stopPropagation()}>
                                {/* {pageType === 'PDP' ? `${monthlyCostStrippedZeros}/mo` : `${monthlyCostStrippedZeros} / mo`} */}
                                {selectedFinancingOffer.isLTO
                                    ? `${limitedFinancingMessage &&
                                    limitedFinancingMessage.replace(
                                        '{numOfMonths}',
                                        selectedFinancingOffer.numOfMonths
                                    )}${(pageType === 'PDP' && validThroughDate)}`
                                    : everydayFinancingMessage &&
                                    everydayFinancingMessage
                                        .replace('{monthlyCostStrippedZeros}', monthlyCostStrippedZeros)
                                        .replace('{numOfMonths}', selectedFinancingOffer.numOfMonths)}
                                {/* ? `0% Interest with ${
                                           selectedFinancingOffer.numOfMonths
                                      } equal monthly payments. Limited Time.${(pageType === 'PDP' &&
                                         ` Valid thru ${selectedFinancingOffer.endDate} `) ||
                                          ''}`
                                       :`Suggested payments with ${selectedFinancingOffer.numOfMonths} months special financing. `} */}
                                <a role='button' className='financing-price-modal-toggle' onClick={openModal} href='javascript:void(0)'>
                                    <span className='learn-how'>Learn How</span>
                                    <span className='prevent-wrap'>
                                        <span aria-hidden='true'></span>
                                    </span>
                                </a>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    ) : (
        <div className={`msc-product__financing-per-month${selectedFinancingOffer ? '' : ' no-financing'}`}>
            <div className='product-price-section'>
                {/* VSI Customization - START */}
                <div className='product-price-label'>{pageType === 'PLP' ? (sizeValue ? sizeValue : 'Starting at') : null}</div>
                {/* VSI Customization - END */}
                <div className='product-price'>{priceComponent || `${context.cultureFormatter.formatCurrency(sellingPrice)}`}</div>
            </div>
            {selectedFinancingOffer && (
                <>
                    <div className='partition'>
                        <span className='or_text'>OR</span>
                    </div>
                    {campaign && campaignRemainingCost ? (
                        <div className='financing-price-section'>
                            {/* commented as per story no 21902 */}
                            {/* <div className='financing-price-label'>Pay as low as</div> */}
                            <div className='financing-price financing-price-right'>{`$${Math.ceil(
                                campaign.initialMonthlyAmount
                            )}/mo for the first ${campaign.numOfDiscountedMonths} months`}</div>
                            {/* tslint:disable-next-line: react-this-binding-issue jsx-no-lambda react-a11y-event-has-role*/}
                            <div className='financing-price-info financing-price-text' onClick={e => e.stopPropagation()}>
                                {/* {`Then $${Math.ceil(campaignRemainingCost.cost)}/mo for ${
                                    campaignRemainingCost?.months
                                } months with 0% APR with Mattress Firm Credit Card.Limited Time.`} */}
                                {limitedCampaignFinancingMessage &&
                                    limitedCampaignFinancingMessage
                                        .replace('{campaignCost}', campaignCostStrippedZeros)
                                        .replace('{campaignMonths}', campaignRemainingCost?.months)}
                                <a role='button' className='financing-price-modal-toggle' onClick={openModal} href='javascript:void(0)'>
                                    Learn How
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className='financing-price-section'>
                            {/* tslint:disable-next-line: react-this-binding-issue jsx-no-lambda react-a11y-event-has-role*/}
                            <div className='financing-price financing-price-right'>
                                {pageType === 'PLP' ? `${monthlyCostStrippedZeros}/mo` : `${monthlyCostStrippedZeros} / mo`}
                            </div>
                            <div className='financing-price-info financing-price-text' onClick={e => e.stopPropagation()}>
                                {selectedFinancingOffer.isLTO
                                    ? `${limitedFinancingMessage &&
                                    limitedFinancingMessage.replace(
                                        '{numOfMonths}',
                                        selectedFinancingOffer.numOfMonths
                                    ).replace('{monthlyCostStrippedZeros}', monthlyCostStrippedZeros)
                                    }`
                                    : everydayFinancingMessage &&
                                    everydayFinancingMessage
                                        .replace('{numOfMonths}', selectedFinancingOffer.numOfMonths)
                                        .replace('{monthlyCostStrippedZeros}', monthlyCostStrippedZeros)
                                }
                                <a role='button' className='financing-price-modal-toggle' onClick={openModal} href='javascript:void(0)'>
                                    Learn How
                                </a>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
